import { get } from "../api.js";
import type { ReportQuery, ReportingDataSource, ReportingPeriod } from "./staff.js";

export type AttendanceMetric =
  | "all"
  | "attended"
  | "late"
  | "cancelled"
  | "no_show"
  | "scheduled"
  | "other";

export type AttendanceStudentFilter = "all" | "no_show" | "cancelled";

export interface AttendanceSummary {
  scheduled: number;
  attended: number;
  late: number;
  cancelled: number;
  no_show: number;
  other: number;
  attendance_rate_percent: string | null;
  cancellation_rate_percent: string | null;
  no_show_rate_percent: string | null;
  attendance_rate_denominator: number;
  cancellation_rate_denominator: number;
  no_show_rate_denominator: number;
  denominator_note: string;
  coverage_note: string;
}

export interface AttendanceTrendRow {
  bucket_start: string;
  bucket_end: string;
  scheduled: number;
  attended: number;
  late: number;
  cancelled: number;
  no_show: number;
  other: number;
  attendance_rate_percent: string | null;
}

export interface AttendanceStudentRow {
  portal_student_id: string;
  student_name: string | null;
  scheduled: number;
  attended: number;
  late: number;
  cancelled: number;
  no_show: number;
  last_lesson_at: string | null;
}

export interface AttendanceAnalyticsReport {
  period: ReportingPeriod;
  data_sources: ReportingDataSource[];
  summary: AttendanceSummary;
  trend_granularity: "day" | "week";
  trend: AttendanceTrendRow[];
  by_student_total: number;
  by_student_page: number;
  by_student_page_size: number;
  by_student: AttendanceStudentRow[];
}

export interface AttendanceLessonRow {
  hub_appointment_id: string;
  amelia_appointment_id: number | null;
  portal_student_id: string;
  student_name: string | null;
  provider_id: number | null;
  tutor_id: string | null;
  service_id: number | null;
  service_name: string | null;
  starts_at: string;
  attendance_status: Exclude<AttendanceMetric, "all">;
}

export interface AttendanceDetailPage {
  period: ReportingPeriod;
  data_sources: ReportingDataSource[];
  total: number;
  page: number;
  page_size: number;
  items: AttendanceLessonRow[];
}

function queryParams(query: ReportQuery): Record<string, string> {
  return {
    start_date: query.start_date,
    end_date: query.end_date,
    scope: query.scope,
    ...(query.provider_id === undefined ? {} : { provider_id: String(query.provider_id) }),
  };
}

/**
 * Attendance analytics contract added 3 September 2026.
 *
 * WMF-SIte owns all status classification, denominators, threshold filtering,
 * pagination and provider scoping. This client only renders the returned read
 * model; it never fetches a wider organisation result and divides it up in the
 * browser.
 */
export function attendanceAnalytics(
  query: ReportQuery,
  options: {
    studentFilter?: AttendanceStudentFilter;
    studentPage?: number;
    studentPageSize?: number;
  } = {},
): Promise<AttendanceAnalyticsReport> {
  return get<AttendanceAnalyticsReport>(
    "/staff-api/api/v1/reports/attendance/analytics",
    {
      ...queryParams(query),
      ...(options.studentFilter ? { student_filter: options.studentFilter } : {}),
      ...(options.studentPage ? { student_page: String(options.studentPage) } : {}),
      ...(options.studentPageSize
        ? { student_page_size: String(options.studentPageSize) }
        : {}),
    },
  );
}

export function attendanceDetails(
  query: ReportQuery,
  options: {
    portalStudentId?: string;
    metric?: AttendanceMetric;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<AttendanceDetailPage> {
  return get<AttendanceDetailPage>(
    "/staff-api/api/v1/reports/attendance/details",
    {
      ...queryParams(query),
      ...(options.portalStudentId ? { portal_student_id: options.portalStudentId } : {}),
      ...(options.metric ? { metric: options.metric } : {}),
      ...(options.page ? { page: String(options.page) } : {}),
      ...(options.pageSize ? { page_size: String(options.pageSize) } : {}),
    },
  );
}
